import { logger } from './logger'

import type { DealerUser } from '../types/dealer-user'

const AUTH_TOKEN_KEY = 'dealer_auth_token'
const REFRESH_TOKEN_KEY = 'dealer_refresh_token'
const USER_DATA_KEY = 'dealer_user_data'
const TOKEN_EXPIRES_AT_KEY = 'dealer_token_expires_at'
const PROXY_SESSION_KEY = 'dealer_proxy_session'

/**
 * 슈퍼관리자 대행 접속(#273) — 관리자 백오피스가 연 이 탭에서만 쓰는 접속. 탭 보관(sessionStorage)에 두어
 * 같은 브라우저의 실제 딜러 로그인(localStorage)과 서로 덮어쓰지 않고, 탭을 닫으면 사라진다.
 */
export interface DealerProxySessionInfo {
  proxySessionId: string
  adminUsername: string
  /** ISO time the server gave — the session ends then (the length is set on the server only). */
  expiresAt: string
}

function readProxySession(): DealerProxySessionInfo | null {
  try {
    const raw = sessionStorage.getItem(PROXY_SESSION_KEY)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- written by storeProxySessionAuth below
    return raw ? (JSON.parse(raw) as DealerProxySessionInfo) : null
  } catch {
    return null
  }
}

/** Where this tab keeps its session: the tab itself during a proxy session, the browser otherwise. */
function authStorage(): Storage {
  return readProxySession() ? sessionStorage : localStorage
}

export function getProxySession(): DealerProxySessionInfo | null {
  return readProxySession()
}

export function isProxySession(): boolean {
  return readProxySession() !== null
}

export function storeTokenExpiresAt(expiresIn: number): void {
  const tokenExpiresAt = Date.now() + expiresIn * 1000
  authStorage().setItem(TOKEN_EXPIRES_AT_KEY, tokenExpiresAt.toString())
}

export function getTokenExpiresAt(): number | null {
  const value = authStorage().getItem(TOKEN_EXPIRES_AT_KEY)

  if (!value) {
    return null
  }

  return Number.parseInt(value, 10)
}

export function storeAuthData(
  accessToken: string,
  refreshToken: string,
  user: DealerUser,
  expiresIn: number,
): void {
  const storage = authStorage()
  storage.setItem(AUTH_TOKEN_KEY, accessToken)
  storage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  storage.setItem(USER_DATA_KEY, JSON.stringify(user))
  storeTokenExpiresAt(expiresIn)
}

/** Tokens from a refresh, kept where the session lives. */
export function storeRefreshedTokens(accessToken: string, refreshToken: string): void {
  const storage = authStorage()
  storage.setItem(AUTH_TOKEN_KEY, accessToken)
  storage.setItem(REFRESH_TOKEN_KEY, refreshToken)
}

/** A proxy session has no refresh token: the access token lasts until the session ends. */
export function storeProxySessionAuth(
  accessToken: string,
  user: DealerUser,
  expiresIn: number,
  info: DealerProxySessionInfo,
): void {
  sessionStorage.setItem(PROXY_SESSION_KEY, JSON.stringify(info))
  sessionStorage.setItem(AUTH_TOKEN_KEY, accessToken)
  sessionStorage.removeItem(REFRESH_TOKEN_KEY)
  sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(user))
  storeTokenExpiresAt(expiresIn)
}

export function clearAuthData(): void {
  const storage = authStorage()
  storage.removeItem(AUTH_TOKEN_KEY)
  storage.removeItem(REFRESH_TOKEN_KEY)
  storage.removeItem(USER_DATA_KEY)
  storage.removeItem(TOKEN_EXPIRES_AT_KEY)
  try {
    sessionStorage.removeItem(PROXY_SESSION_KEY)
  } catch {
    // sessionStorage unavailable — nothing was kept there
  }
}

export function getStoredToken(): string | null {
  return authStorage().getItem(AUTH_TOKEN_KEY)
}

export function getStoredRefreshToken(): string | null {
  return authStorage().getItem(REFRESH_TOKEN_KEY)
}

export function getStoredUser(): DealerUser | null {
  const userDataString = authStorage().getItem(USER_DATA_KEY)

  if (!userDataString) {
    return null
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- JSON.parse returns any, cast needed for localStorage data
    return JSON.parse(userDataString) as DealerUser
  } catch (error_) {
    logger.error('[AuthStorage] Failed to parse dealer user data:', error_)
    return null
  }
}

/** Where a tab goes when its session is over — a proxy tab cannot sign in again by itself. */
export const PROXY_SESSION_ENDED_PATH = '/proxy-access?ended=1'
